import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  TablePagination,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import {
  BodyMeasurement,
  deleteBodyMeasurement,
  listBodyMeasurements,
} from "../../services/BodyMeasurementService";
import { ConfirmationDialog } from "../../utils/ConfirmationDialog";

interface BodyMeasurementHistoryDrawerProps {
  open: boolean;
  userId: string;
  onClose: () => void;
  /** Called after a delete so the parent can refresh the latest-scan card. */
  onChanged?: () => void;
}

const dash = (value?: number | null, unit?: string) =>
  value === undefined || value === null
    ? "—"
    : `${value}${unit ? ` ${unit}` : ""}`;

/** Every scan attribute, in display order, for one measurement. */
const metricsFor = (
  row: BodyMeasurement
): { label: string; value: string }[] => [
  { label: "Weight", value: dash(row.weightKg, "kg") },
  { label: "Skeletal muscle", value: dash(row.skeletalMuscleKg, "kg") },
  { label: "Body fat", value: dash(row.bodyFatKg, "kg") },
  { label: "Body fat %", value: dash(row.bodyFatPct, "%") },
  { label: "Muscle %", value: dash(row.skeletalMusclePct, "%") },
  { label: "BMI", value: dash(row.bmi) },
  { label: "Visceral fat", value: dash(row.visceralFatLevel) },
  {
    label: "Body age",
    value:
      row.bodyAge !== undefined && row.bodyAge !== null
        ? `${row.bodyAge} yrs`
        : "—",
  },
  { label: "RMR", value: dash(row.restingMetabolismKcal, "kcal") },
  { label: "Height", value: dash(row.heightCm, "cm") },
];

const BodyMeasurementHistoryDrawer: React.FC<
  BodyMeasurementHistoryDrawerProps
> = ({ open, userId, onClose, onChanged }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<BodyMeasurement[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<BodyMeasurement | null>(
    null
  );

  const fetchRows = async () => {
    try {
      setLoading(true);
      const pageData = await listBodyMeasurements({
        userId,
        page,
        size: rowsPerPage,
      });
      setRows(pageData?.content ?? []);
      setTotal(pageData?.totalElements ?? 0);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page, rowsPerPage]);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteBodyMeasurement(pendingDelete.id);
      enqueueSnackbar("Measurement deleted", { variant: "success" });
      onChanged?.();
      if (rows.length === 1 && page > 0) {
        setPage((prev) => prev - 1);
      } else {
        fetchRows();
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.data ||
        error?.response?.data?.message ||
        "Failed to delete measurement";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: 520 },
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6">Measurement history</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" py={4}>
            No measurements recorded yet.
          </Typography>
        ) : (
          rows.map((row) => (
            <Box
              key={row.id}
              sx={{
                borderRadius: 2,
                p: 2,
                mb: 2,
                backgroundColor: theme.palette.background.paper,
                boxShadow: "0 1px 6px rgba(0,0,0,0.4)",
              }}
            >
              <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
                mb={1.5}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {dayjs(row.measuredAt).format("MMM D, YYYY")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dayjs(row.measuredAt).format("h:mm A")}
                    {row.measuredBy ? ` · by ${row.measuredBy}` : ""}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setPendingDelete(row)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 1,
                }}
              >
                {metricsFor(row).map((metric) => (
                  <Box
                    key={metric.label}
                    sx={{
                      borderRadius: 1.5,
                      px: 1.25,
                      py: 1,
                      backgroundColor: theme.palette.background.default,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      display="block"
                    >
                      {metric.label}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {metric.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {row.note && (
                <Box mt={1.5}>
                  <Typography variant="caption" color="text.secondary">
                    Note
                  </Typography>
                  <Typography variant="body2">{row.note}</Typography>
                </Box>
              )}
            </Box>
          ))
        )}
      </Box>

      {!loading && rows.length > 0 && (
        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20]}
          />
        </Box>
      )}

      <ConfirmationDialog
        open={Boolean(pendingDelete)}
        heading="Delete measurement"
        description={
          pendingDelete
            ? `Delete the scan from ${dayjs(pendingDelete.measuredAt).format(
                "MMM D, YYYY"
              )}? This cannot be undone.`
            : ""
        }
        confirmLabel="Delete"
        onProceed={handleDelete}
        handleClose={() => setPendingDelete(null)}
      />
    </Drawer>
  );
};

export default BodyMeasurementHistoryDrawer;
